import React, { useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { NeedsHousehold, PageHeader, useHousehold, useLoadInto, useNotify } from '../common';
import { useGoBack } from '../editing';

const HELP = [
  'Google: em calendar.google.com › Definições › escolhe o calendário › Integrar calendário › "Endereço secreto em formato iCal".',
  'Outlook / Microsoft 365: outlook.com › Definições › Calendário › Calendários partilhados › Publicar um calendário › "Pode ver todos os detalhes" › copia o link ICS.',
  'iCloud: na app Calendário, partilha o calendário como "Calendário público" e copia o link (começa por webcal://).',
  'Quem tem o link vê o calendário todo: a app guarda-o cifrado e nunca o mostra.',
];

/** Link a calendar by its secret iCal address (/casa/eventos/calendarios/novo) or change one (/…/:feedId). */
const CalendarFeedEditPage: React.FC = () => {
  const { feedId } = useParams();
  const { members } = useHousehold();
  const notify = useNotify();
  const goBack = useGoBack('/casa/eventos/calendarios');
  const isNew = !feedId;

  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [owner, setOwner] = useState<T.Guid | null>(null);
  const [busyOnly, setBusyOnly] = useState(false);
  // Someone else's calendar: only its name changes here
  const [canManage, setCanManage] = useState(true);

  // The household has a handful of calendars: the list is the one call
  const loadFeed = async (id: T.Guid) => {
    const feed = (await api.getCalendarFeeds()).find(f => f.publicId === id);
    if (!feed) throw new Error('Este calendário já não está ligado.');
    return feed;
  };
  const { loading, error, setError } = useLoadInto(feedId ? () => loadFeed(feedId) : null, feed => {
    setName(feed.name);
    setOwner(feed.ownerMemberPublicId ?? null);
    setBusyOnly(feed.busyOnly);
    setCanManage(feed.canManage);
  }, [feedId]);

  const save = async () => {
    if (!name.trim()) { setError('Dá um nome ao calendário.'); return; }
    if (isNew && !url.trim()) { setError('Cola o endereço secreto do calendário.'); return; }
    const request: T.SaveCalendarFeedRequest = { name: name.trim(), url: url.trim() || null, ownerMemberPublicId: owner, busyOnly };
    setBusy(true);
    setError(null);
    try {
      // The server reads the calendar before answering: a link that does not work is refused with the reason
      const saved = feedId ? await api.updateCalendarFeed(feedId, request) : await api.createCalendarFeed(request);
      notify(saved.lastError ?? `${saved.name}: ${saved.eventCount} eventos lidos`, { severity: saved.lastError ? 'warning' : undefined });
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 640, pb: 10 }}>
      <PageHeader title={isNew ? 'Ligar calendário' : 'Editar calendário'} />
      <Stack spacing={2}>
        <TextField
          label="Nome" placeholder="Ex.: Trabalho do Hugo, Família" value={name} autoFocus={isNew}
          onChange={e => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 60 } }}
        />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>De quem é</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Partilhado (da casa)" clickable={canManage} disabled={!canManage && owner !== null} color="primary"
              variant={owner === null ? 'filled' : 'outlined'} onClick={canManage ? () => setOwner(null) : undefined}
            />
            {members.map(m => (
              <Chip
                key={m.publicId} label={m.name} clickable={canManage} disabled={!canManage && owner !== m.publicId} color="primary"
                variant={owner === m.publicId ? 'filled' : 'outlined'} onClick={canManage ? () => setOwner(m.publicId) : undefined}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            No partilhado, um evento é de quem tiver o nome no título ("Tomás - festa da creche"); sem nome, é da casa.
          </Typography>
        </Box>
        {!canManage && (
          <Alert severity="info">
            Só quem ligou este calendário, ou a pessoa de quem ele é, pode mudar de quem é, o link e o "só ocupado". Aqui podes mudar o nome.
          </Alert>
        )}
        <TextField
          label={isNew ? 'Endereço secreto em formato iCal' : 'Novo endereço (opcional)'} placeholder="https://… ou webcal://…" value={url}
          disabled={!canManage}
          helperText={isNew ? undefined : 'Deixa vazio para manter o link que já está.'}
          onChange={e => setUrl(e.target.value)} slotProps={{ htmlInput: { spellCheck: false, autoComplete: 'off', inputMode: 'url' } }}
        />
        <FormControlLabel
          control={<Switch checked={busyOnly} disabled={!canManage} onChange={e => setBusyOnly(e.target.checked)} />}
          label={<Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Mostrar só como ocupado</Typography>
            <Typography variant="caption" color="text.secondary">Sem título nem local (um calendário do trabalho)</Typography>
          </Box>}
        />
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Onde encontro o endereço?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {HELP.map(line => <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>)}
            </Stack>
          </AccordionDetails>
        </Accordion>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>

      <Paper
        elevation={6}
        sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 2, p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', zIndex: 2 }}
      >
        <Button onClick={goBack} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? <CircularProgress size={20} color="inherit" /> : isNew ? 'Ligar e ler' : 'Guardar e ler'}
        </Button>
      </Paper>
    </Box>
  );
};

const CalendarFeedEditRoute: React.FC = () => <NeedsHousehold><CalendarFeedEditPage /></NeedsHousehold>;
export default CalendarFeedEditRoute;
